import { AnalyticIdentifier } from '@common/neo.tax';
import {
  Button,
  ModalActions,
  ModalHeader,
  Stack,
  Typography,
  getRecipeColor,
  ControlledButtonGroup,
  Indent,
  ControlledCheckbox,
  colors,
} from '@ui-components/neo.tax';
import { ReactComponent as Briefcase } from '@ui-components/neo.tax/dist/icons/briefcase.svg';
import { useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { useUpdateRdProject, RdProject } from '@neo.tax/rd/hooks';
import { ProjectPurpose, useAnalyticId, ProjectEligibilityModal, HIGHLIGHT_VARIANT } from '@neo.tax/shared';

import { RdProjectPurposeForm, useImprovedAspectsOptions, useRdProjectPurposeForm } from './hooks';
import { RD_PROJECT_PURPOSE_IMPROVED_ASPECTS } from './utils';

interface RdProjectPurposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  rdProject: RdProject;
}

const RD_PROJECT_PURPOSE_OPTIONS: { value: ProjectPurpose; label: string }[] = [
  { value: ProjectPurpose.CreateNewProduct, label: 'Create new product/process' },
  { value: ProjectPurpose.ImproveExistingProduct, label: 'Improve existing product/process' },
];

export const RdProjectPurposeModal = ({
  isOpen,
  onClose,
  rdProject,
}: RdProjectPurposeModalProps): React.ReactElement => {
  const [updateRdProject] = useUpdateRdProject({ rdProject, includeOptimisticResponse: true });
  const { control, handleSubmit, reset, watch } = useRdProjectPurposeForm({ rdProject });
  const [purpose] = watch(['purpose']);

  const purposeId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_PROJECT_PURPOSE);
  const improvedAspectsOptions = useImprovedAspectsOptions();

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<RdProjectPurposeForm> = async (data) => {
    const improvedAspects = RD_PROJECT_PURPOSE_IMPROVED_ASPECTS.filter(
      (improvedAspect) => !!data.improvedAspects[improvedAspect],
    );
    await updateRdProject({
      purpose: data.purpose,
      improvedAspects: data.purpose === ProjectPurpose.ImproveExistingProduct ? improvedAspects : [],
    });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, rdProject.purpose, reset]);

  return (
    <ProjectEligibilityModal isOpen={isOpen}>
      <Stack as="form" direction="column" gap={8} onSubmit={handleSubmit(onSubmit)} paddingX={4}>
        <ModalHeader
          icon={<Briefcase width={24} color={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').text} />}
          iconBgColor={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').bg}
          title={
            <Typography fontSize="base" fontWeight="semibold">
              Project{' '}
              <Typography
                fontSize="inherit"
                fontWeight="inherit"
                color={getRecipeColor(HIGHLIGHT_VARIANT, 'whiteBg').text}
              >
                {rdProject.name}
              </Typography>{' '}
              Purpose
            </Typography>
          }
          copy="Define the specific business component and what was improved."
        />
        <Stack direction="column">
          <ControlledButtonGroup
            label="What was the purpose of this project?"
            id={purposeId}
            name="purpose"
            options={RD_PROJECT_PURPOSE_OPTIONS}
            control={control}
            optionsGrow
          />
          {purpose === ProjectPurpose.ImproveExistingProduct && (
            <Indent>
              <Stack direction="column" gap={2}>
                <Typography fontSize="sm" fontWeight="medium" color={colors.gray[400]}>
                  What aspect was improved?
                </Typography>
                <Stack direction="column" gap={5}>
                  {improvedAspectsOptions.map(({ id, label, value }) => (
                    <ControlledCheckbox
                      key={id}
                      control={control}
                      name={`improvedAspects.${value}`}
                      id={id}
                      label={label}
                    />
                  ))}
                </Stack>
              </Stack>
            </Indent>
          )}
        </Stack>
        <ModalActions hasTopBorder={false}>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" rounded="regular" size="base" type="submit">
            Save
          </Button>
        </ModalActions>
      </Stack>
    </ProjectEligibilityModal>
  );
};
